import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  alpha,
} from "@mui/material";
import {
  HiOutlineBars3,
  HiOutlineArrowRightOnRectangle,
} from "react-icons/hi2";
import ConfirmDialog from "../common/ConfirmDialog";
import fetching from "../../helpers/fetching/fetching";
import fireAlert from "../../helpers/alerts/fireAlert";
import {
  removeUserToken,
  removeUserData,
} from "../../redux/features/user/userSlice";
import { UserState } from "../../types/types";
import { APP_NAME } from "../../utils/app-info";
import { actionIconButtonSx, APP_BAR_HEIGHT } from "./layoutStyles";

type Props = {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
};

const AppHeader = ({ onMenuClick, showMenuButton = false }: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userToken = useSelector((state: UserState) => state.user.userToken);
  const userData = useSelector((state: UserState) => state.user.userData);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const userName = userData?.name || userData?.email || "المسؤول";

  const handleLogout = async () => {
    setLoading(true);
    try {
      await fetching(`/logout`, userToken, "POST").then((res) => res.json());
    } catch {
      // proceed with local logout
    } finally {
      dispatch(removeUserToken());
      dispatch(removeUserData());
      fireAlert("تم تسجيل الخروج بنجاح", "success");
      navigate("/login");
      setLoading(false);
      setLogoutOpen(false);
    }
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          width: "100%",
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Toolbar
          sx={{
            width: "100%",
            gap: 2,
            minHeight: APP_BAR_HEIGHT,
            height: APP_BAR_HEIGHT,
            px: { xs: 2, sm: 3, md: 3.5 },
          }}
        >
          {showMenuButton && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={onMenuClick}
              sx={{
                display: { md: "none" },
                ...actionIconButtonSx,
                color: "text.secondary",
              }}
            >
              <HiOutlineBars3 size={22} />
            </IconButton>
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1rem", md: "1.125rem" },
                color: "text.primary",
              }}
            >
              {APP_NAME}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: { xs: "none", sm: "block" } }}
            >
              لوحة إدارة دار شير
            </Typography>
          </Box>
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-label="user menu"
            sx={{
              p: 0.5,
              ...actionIconButtonSx,
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: "primary.main",
                fontSize: "0.9375rem",
                fontWeight: 700,
                border: 2,
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
              }}
            >
              {userName.charAt(0)}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 200,
                  mt: 1,
                  borderRadius: 2,
                  border: 1,
                  borderColor: "divider",
                  boxShadow: 4,
                },
              },
            }}
          >
            <MenuItem disabled sx={{ opacity: "1 !important", py: 1.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {userName}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setLogoutOpen(true);
              }}
              sx={{ py: 1.25, gap: 1.5, color: "error.main" }}
            >
              <HiOutlineArrowRightOnRectangle size={20} />
              تسجيل الخروج
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <ConfirmDialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onConfirm={handleLogout}
        title="تسجيل خروج"
        confirmLabel="تسجيل الخروج"
        loading={loading}
      >
        <Typography variant="body2">
          هل أنت متأكد أنك تريد تسجيل الخروج؟
        </Typography>
      </ConfirmDialog>
    </>
  );
};

export default AppHeader;
